import { useDebounceFn } from '@vueuse/core';
import type { FeatureLike } from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import Select from 'ol/interaction/Select';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';

import type { NotificationResultDTO } from '@nora-monorepo/nora-api-client';

import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import { BBOX_RELOAD_DEBOUNCE_MS, BBOX_RELOAD_MAX_WAIT_MS } from '#nora-ui/map/map/constants';
import { ClusterableLayer } from '#nora-ui/map/map/layer-managers/ClusterableLayer';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import type { SelectedOverlayFile } from '#nora-ui/map/map/state-managers/FileStateManager';

export class NotificationLayerManager extends ClusterableLayer {
  readonly LAYER_ID = 'notificationLayer';
  readonly LAYER_NAME = 'NORA Notifications';
  override readonly isClickable = true;

  private selectInteraction: Select;

  protected override CLUSTER_ICON_NAME = 'notifications';

  protected override CLUSTER_STROKE_COLOR = '#FFB800';

  private debouncedLoadCurrentBbox = useDebounceFn(async (): Promise<void> => {
    await this.map.state.notification.loadCurrentBbox();
  }, BBOX_RELOAD_DEBOUNCE_MS, {
    maxWait: BBOX_RELOAD_MAX_WAIT_MS,
  });

  constructor(map: NoraMap) {
    super(map);

    this.selectInteraction = new Select({
      layers: [this.layer],
      style: this.getSelectedStyle(),
    });
  }

  override async setup(): Promise<void> {
    await super.setup();
    this.setupNotificationClickHandler();
    await this.setupPanHandler();
  }

  override getStyleForSingleFeature(_feature: FeatureLike): Style {
    return new Style({
      image: new CircleStyle({
        radius: 24,
        stroke: new Stroke({
          color: '#FFB800',
          width: 4,
        }),
        fill: new Fill({
          color: '#1F1000',
        }),
      }),
      text: new Text({
        text: 'notifications',
        font: '24px "Material Icons"',
        fill: new Fill({ color: '#FFB800' }),
      }),
    });
  }

  private async setupPanHandler(): Promise<void> {
    this.map.getOlMapOrThrow().on('moveend', async () => {
      await this.debouncedLoadCurrentBbox();
    });
  }

  private setupNotificationClickHandler(): void {
    const olMap = this.map.getOlMapOrThrow();
    olMap.addInteraction(this.selectInteraction);

    this.selectInteraction.on('select', async (event) => {
      const selectedFeature = event.selected.at(0);

      if (!selectedFeature) {
        this.map.state.file.clearSelectedFile();

        return;
      }

      this.onSelectNotification(selectedFeature);
    });
  }

  clearSelectedNotification(): void {
    this.selectInteraction.getFeatures().clear();
  }

  private onSelectNotification(feature: FeatureLike): void {
    // Clustered: unwrap the Cluster feature; unclustered: the feature is the notification itself.
    const subFeatures = feature.get('features') as FeatureLike[] | undefined;
    const selectedSubFeature = subFeatures?.[0] ?? feature;

    const { id, domain, domainId, path } = getFeatureProperties<SelectedOverlayFile>(selectedSubFeature);
    if (id === undefined || domain === undefined || domainId === undefined || path === undefined) {
      return;
    }

    this.map.state.file.setSelectedFile({ id, domain, domainId, path });
  }

  /**
   * Load/reload notification layer features from state.
   * Renders notifications from the state manager's notifications array.
   */
  override load(): void {
    this.unload();

    const notifications = this.map.state.notification.notifications;

    if (notifications.length === 0) {
      return;
    }

    const notificationsGeojson = this.convertToGeoJson(notifications);

    this.source.addFeatures(
      new GeoJSON().readFeatures(notificationsGeojson, {
        dataProjection: MapSetup.WGS84,
        featureProjection: MapSetup.WEB_MERCATOR,
      }),
    );
  }

  /**
   * Unload/clear all notification layer features.
   */
  override unload(): void {
    this.source.clear();
  }

  setVisibility(visible: boolean): void {
    this.layer.setVisible(visible);
  }

  private getSelectedStyle(): Style {
    return new Style({
      text: new Text({
        text: 'notifications',
        font: '20px "Material Icons"',
        fill: new Fill({ color: 'red' }),
      }),
    });
  }

  private convertToGeoJson(notifications?: NotificationResultDTO[]): GeoJSON.FeatureCollection {
    if (!notifications) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    const features: GeoJSON.Feature[] = [];

    notifications.forEach((notification) => {
      if (!notification.location) {
        return;
      }

      features.push({
        type: 'Feature',
        properties: {
          id: notification.id,
          title: notification.title,
          body: notification.body,
          userId: notification.userId,
          isRead: notification.isRead,
          action: notification.action,
          actionContext: notification.actionContext,
          createdAt: notification.createdAt,
          updatedAt: notification.updatedAt,
          deletedAt: notification.deletedAt,
        },
        geometry: notification.location as GeoJSON.Point,
      });
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }
}
